// Utilities
import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import { routes } from "./utils/constants";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
    ThemeProvider,
    CssBaseline,
    Snackbar,
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import getTheme from './theme';

// Pages
import MainDrawer from "./pages/drawer/MainDrawer";
import { getUserToken, getUser } from "./store/slices/userSlice";
import { getSelectedCompanyId, setSelectedCompany, clearSelectedCompany } from "./store/slices/tenantSlice";
import { isSuperAdmin } from "./utils/permissions";
import Login from "./pages/login/Login";
import SuperAdminCompanyPicker from "./pages/companies/SuperAdminCompanyPicker";
import UsersCompanyPicker from "./pages/companies/UsersCompanyPicker";
import AnimatedSplashScreen from "./components/AnimatedSplashScreen";
import { useDispatch, useSelector } from "react-redux";
import CookiesBanner from "./components/ck/Cookie";
import { selectThemeMode } from "./store/slices/themeSlice";
import StaleBuildDialog from "./components/StaleBuildDialog";
import DevModeRibbon from "./components/DevModeRibbon";

function App() {
    const dispatch = useDispatch()
    const accessToken = useSelector(getUserToken)
    const user = useSelector(getUser)
    const selectedCompanyId = useSelector(getSelectedCompanyId)
    const superAdmin = isSuperAdmin(user)
    const memberships = Array.isArray(user.companies) ? user.companies : []
    const membershipKey = memberships.map((c) => c.id).join(',')
    const multiCompanyUser = !superAdmin && memberships.length > 1

    // Super-admin or multi-company member must pick (or re-pick) before entering the app.
    const needsCompanySelection = (superAdmin || multiCompanyUser) && !selectedCompanyId

    // Single-company members: auto-select their home company for UX consistency.
    // Multi-company: selection must match JWT company_id (source of truth after switch).
    useEffect(() => {
        if (!accessToken || superAdmin || !user?.id) return;

        if (memberships.length === 1) {
            const only = memberships[0];
            if (selectedCompanyId !== only.id) {
                dispatch(setSelectedCompany({ id: only.id, name: only.name }));
            }
            return;
        }

        if (memberships.length > 1) {
            if (selectedCompanyId && selectedCompanyId !== user.company_id) {
                dispatch(clearSelectedCompany());
                return;
            }
            if (selectedCompanyId && !memberships.some((c) => c.id === selectedCompanyId)) {
                dispatch(clearSelectedCompany());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- membershipKey captures membership identity
    }, [accessToken, superAdmin, user?.id, user?.company_id, membershipKey, selectedCompanyId, dispatch]);

    const [showSplash, setShowSplash] = useState(true)
    const themeMode = useSelector(selectThemeMode)
    const theme = getTheme(themeMode)
    
    // Auto-refresh state management
    const initialLoadTime = useRef(performance.now())
    const [showRefreshNotification, setShowRefreshNotification] = useState(false)
    const [countdown, setCountdown] = useState(30)
    const refreshIntervalRef = useRef(null)
    const countdownIntervalRef = useRef(null)

    // Prompt to refresh when returning after the tab/app was away a while.
    // Skips brief tab switches, initial load, and splash; never auto-reloads.
    const MIN_HIDDEN_MS = 10 * 60 * 1000
    const [showResumeRefreshDialog, setShowResumeRefreshDialog] = useState(false)
    const hiddenAtRef = useRef(null)
    const resumeDialogOpenRef = useRef(false)
    const showSplashRef = useRef(showSplash)
    showSplashRef.current = showSplash

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hiddenAtRef.current = Date.now()
                return
            }
            if (document.visibilityState !== 'visible' || hiddenAtRef.current == null) {
                return
            }

            const hiddenFor = Date.now() - hiddenAtRef.current
            hiddenAtRef.current = null

            if (
                showSplashRef.current ||
                resumeDialogOpenRef.current ||
                hiddenFor < MIN_HIDDEN_MS
            ) {
                return
            }

            resumeDialogOpenRef.current = true
            setShowResumeRefreshDialog(true)
        }

        document.addEventListener('visibilitychange', onVisibilityChange)
        return () => document.removeEventListener('visibilitychange', onVisibilityChange)
    }, [])

    const dismissResumeRefreshDialog = () => {
        resumeDialogOpenRef.current = false
        setShowResumeRefreshDialog(false)
    }

    // Auto-refresh logic: refresh page after 2 hours
    useEffect(() => {
        const TWO_HOURS_MS = 2 * 60 * 60 * 1000 // 2 hours in milliseconds
        const WARNING_TIME_MS = 30 * 1000 // 30 seconds before refresh
        const CHECK_INTERVAL_MS = 30 * 1000 // Check every 30 seconds
        let notificationShown = false // Track if notification was already triggered
        
        const checkElapsedTime = () => {
            const elapsed = performance.now() - initialLoadTime.current
            const timeUntilRefresh = TWO_HOURS_MS - elapsed
            
            // Show notification when 30 seconds remain (only once)
            if (timeUntilRefresh <= WARNING_TIME_MS && !notificationShown) {
                notificationShown = true // Prevent multiple triggers
                setShowRefreshNotification(true)
                setCountdown(Math.ceil(timeUntilRefresh / 1000))
                
                // Clear the main interval since we're in countdown mode now
                if (refreshIntervalRef.current) {
                    clearInterval(refreshIntervalRef.current)
                    refreshIntervalRef.current = null
                }
                
                // Start countdown interval
                countdownIntervalRef.current = setInterval(() => {
                    const remaining = TWO_HOURS_MS - (performance.now() - initialLoadTime.current)
                    const secondsRemaining = Math.ceil(remaining / 1000)
                    
                    if (secondsRemaining > 0) {
                        setCountdown(secondsRemaining)
                    } else {
                        // Time to refresh
                        clearInterval(countdownIntervalRef.current)
                        window.location.reload()
                    }
                }, 1000)
            }
            
            // Trigger refresh at exactly 2 hours (fallback safety check)
            if (elapsed >= TWO_HOURS_MS) {
                window.location.reload()
            }
        }
        
        // Initial check
        checkElapsedTime()
        
        // Set up interval to check every 30 seconds
        refreshIntervalRef.current = setInterval(checkElapsedTime, CHECK_INTERVAL_MS)
        
        // Cleanup on unmount
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current)
            }
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current)
            }
        }
    }, []) // Empty dependency array - run only once on mount

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <DevModeRibbon />
            <StaleBuildDialog />
            <Dialog
                open={showResumeRefreshDialog}
                onClose={dismissResumeRefreshDialog}
                aria-labelledby="resume-refresh-dialog-title"
            >
                <DialogTitle id="resume-refresh-dialog-title">Refresh page?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You&apos;ve been away for a while. Refresh to load the latest data?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={dismissResumeRefreshDialog}>Not now</Button>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                        autoFocus
                    >
                        Refresh
                    </Button>
                </DialogActions>
            </Dialog>
            {showSplash ? (
                <AnimatedSplashScreen setShowSplash={setShowSplash} />
            ) : (
                <BrowserRouter>
                    <CookiesBanner />
                    <div className="App">
                        {accessToken ?
                            (needsCompanySelection ?
                                (superAdmin ? <SuperAdminCompanyPicker /> : <UsersCompanyPicker />)
                                :
                                <MainDrawer />)
                            :
                            <Routes>
                                <Route path={routes.login} element={<Login />} />
                                <Route path="*" element={<Navigate to={routes.login} />} />
                            </Routes>
                        }
                    </div>
                    <Snackbar
                        open={showRefreshNotification}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        autoHideDuration={null}
                        sx={{
                            bottom: {
                                xs: 'calc(72px + env(safe-area-inset-bottom, 0px))',
                                md: 24,
                            },
                        }}
                    >
                        <Alert severity="info" onClose={() => setShowRefreshNotification(false)}>
                            Page will refresh in {countdown} second{countdown !== 1 ? 's' : ''} to ensure you have the latest updates.
                        </Alert>
                    </Snackbar>
                </BrowserRouter>
            )}
        </ThemeProvider>
    );
}

export default App;
